import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { useDossierDetail, useUploadDocument, useAppointments } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { 
  Loader2, 
  FileText,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Calendar,
  MapPin,
  Eye,
  Download,
  PartyPopper
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// Ouvre un document avec authentification JWT
const handleViewDocument = async (documentId) => {
  try {
    const token = localStorage.getItem('hav_token');
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/file`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Erreur');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error('Impossible de charger le document');
  }
};

const visaLabels = {
  studies: 'Études',
  formation_pro: 'Formation Pro',
  work: 'Travail',
  family_reunification: 'Regroupement familial',
};

const statusConfig = {
  missing: { label: 'Manquant', color: 'bg-slate-100 text-slate-600', icon: AlertCircle },
  pending_review: { label: 'En attente de validation', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  validated: { label: 'Validé', color: 'bg-success/10 text-success', icon: CheckCircle },
  rejected: { label: 'Rejeté - À redéposer', color: 'bg-destructive/10 text-destructive', icon: XCircle },
};

const appointmentStatusConfig = {
  scheduled: { label: 'Planifié', color: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Terminé', color: 'bg-success/10 text-success' },
  to_reschedule: { label: 'À replanifier', color: 'bg-orange-100 text-orange-700' },
};

const StudentDossier = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [uploadingDoc, setUploadingDoc] = useState(null);

  // Get student ID from user context - we need to find the student record
  const { data: dossier, isLoading, error } = useDossierDetail(user?.student_id || 'self');
  const { data: appointments } = useAppointments(dossier?.student_id);
  const uploadDocument = useUploadDocument(dossier?.student_id);

  const onDrop = useCallback(async (acceptedFiles, docKey) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    
    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez PDF, JPG ou PNG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 10MB.');
      return;
    }
    
    setUploadingDoc(docKey);
    try {
      await uploadDocument.mutateAsync({
        documentType: docKey,
        file,
      });
      toast.success('Document téléchargé avec succès ! Il sera examiné sous peu.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingDoc(null);
    }
  }, [uploadDocument]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !dossier) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Dossier non trouvé</h2>
        <p className="text-muted-foreground">
          Contactez l'administration pour activer votre dossier.
        </p>
      </div>
    );
  }

  const isComplete = dossier.status === 'complete' || dossier.status === 'pdf_generated';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-dossier-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Mon dossier</h1>
        <p className="text-muted-foreground mt-1">
          Déposez vos documents pour votre demande de visa {visaLabels[dossier.visa_objective]}
        </p>
      </div>

      {/* Success Banner */}
      {isComplete && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-success/10 to-primary/10">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-success/20 flex items-center justify-center">
                <PartyPopper className="w-6 h-6 text-success" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-success">
                  Félicitations ! Votre dossier est complet !
                </h3>
                <p className="text-sm text-muted-foreground">
                  Tous vos documents ont été validés. L'administration va générer votre dossier final.
                </p>
              </div>
              {dossier.final_pdf_url && (
                <Button asChild>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${dossier.final_pdf_url}`} target="_blank" rel="noopener noreferrer">
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger le dossier
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Overview */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Progression du dossier</span>
                <span className="text-sm text-muted-foreground">
                  {dossier.validated_count} / {dossier.total_required} documents validés
                </span>
              </div>
              <Progress value={dossier.completion_percentage} className="h-3" />
            </div>
            <div className="text-center md:text-right">
              <p className="text-3xl font-bold font-heading text-primary">
                {Math.round(dossier.completion_percentage)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Documents List */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Documents à fournir
              </CardTitle>
              <CardDescription>
                Déposez vos documents au format PDF, JPG ou PNG (max 10MB)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dossier.documents.map((doc) => {
                  const StatusIcon = statusConfig[doc.status]?.icon || AlertCircle;
                  const canUpload = doc.status === 'missing' || doc.status === 'rejected';
                  
                  return (
                    <div 
                      key={doc.key} 
                      className={`p-4 rounded-lg border ${
                        doc.status === 'validated' ? 'bg-success/5 border-success/20' :
                        doc.status === 'rejected' ? 'bg-destructive/5 border-destructive/20' :
                        'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <StatusIcon className={`w-5 h-5 ${
                            doc.status === 'validated' ? 'text-success' :
                            doc.status === 'rejected' ? 'text-destructive' :
                            doc.status === 'pending_review' ? 'text-yellow-600' :
                            'text-muted-foreground'
                          }`} />
                          <div>
                            <p className="font-medium">{doc.label}</p>
                            <Badge className={statusConfig[doc.status]?.color + ' mt-1'}>
                              {statusConfig[doc.status]?.label}
                            </Badge>
                          </div>
                        </div>
                        {doc.document && doc.status !== 'rejected' && (
                          <Button variant="outline" size="sm" asChild>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.document.id}/file`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </a>
                          </Button>
                        )}
                      </div>

                      {/* Rejection reason */}
                      {doc.status === 'rejected' && doc.document?.review_comment && (
                        <div className="mb-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                          <p className="text-sm font-medium text-destructive">Motif du rejet :</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {doc.document.review_comment}
                          </p>
                        </div>
                      )}

                      {/* Upload area */}
                      {canUpload && (
                        <DropzoneArea 
                          docKey={doc.key} 
                          onDrop={onDrop} 
                          isUploading={uploadingDoc === doc.key}
                          label={doc.status === 'rejected' ? 'Re-déposer le document' : 'Déposer le document'}
                        />
                      )}

                      {/* Validated info */}
                      {doc.status === 'validated' && doc.document && (
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex items-center gap-2 text-sm text-success">
                            <CheckCircle className="w-4 h-4" />
                            <span>Validé le {formatDate(doc.document.reviewed_at)}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDocument(doc.document.id)}
                            className="h-7 text-xs"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Voir
                          </Button>
                        </div>
                      )}

                      {/* Pending info */}
                      {doc.status === 'pending_review' && doc.document && (
                        <div className="flex items-center gap-2 text-sm text-yellow-600 mt-2">
                          <Clock className="w-4 h-4" />
                          <span>Déposé le {formatDate(doc.document.uploaded_at)} - En attente de validation</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Appointments Sidebar */}
        <div>
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-secondary" />
                Mes rendez-vous
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!appointments || appointments.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">
                    Aucun rendez-vous planifié
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    L'administration vous contactera pour fixer un rendez-vous
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {appointments.map((appt) => (
                    <div key={appt.id} className="p-4 rounded-lg border bg-secondary/5">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{formatDate(appt.date)}</p>
                          <p className="text-sm text-muted-foreground">{appt.location}</p>
                          <Badge className={appointmentStatusConfig[appt.status]?.color + ' mt-2'}>
                            {appointmentStatusConfig[appt.status]?.label}
                          </Badge>
                          {appt.notes && (
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              {appt.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card className="border-0 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-sm">Conseils</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>• Assurez-vous que vos documents sont lisibles</p>
              <p>• Les photos doivent être nettes et bien éclairées</p>
              <p>• Les PDF ne doivent pas être protégés par mot de passe</p>
              <p>• Taille maximale : 10MB par document</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Dropzone Component
const DropzoneArea = ({ docKey, onDrop, isUploading, label = "Déposer le document" }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, docKey),
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
  });

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
        isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
      }`}
    >
      <input {...getInputProps()} />
      {isUploading ? (
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Téléchargement...</span>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2">
          <Upload className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm">{label}</span>
        </div>
      )}
    </div>
  );
};

export default StudentDossier;
